import { z } from 'zod';
/**
 * Correlation id: el hilo que amarra un mismo hecho de negocio entre sistemas.
 *
 * Hay DOS formas y no son intercambiables:
 *
 * 1. `correlationIdSchema` — UUID. Es el que genera el borde del middleware y
 *    el que exigen nuestros contratos propios (DecoraPOS). Si no es un UUID, es
 *    un error del emisor.
 * 2. `externalCorrelationRefSchema` — texto libre corto. Es el vehiculo en los
 *    sistemas que no manejan UUID: en Altanet el correlation id viaja dentro de
 *    `Numero_documento_respaldo` (p. ej. `"30045"`), y en SAP dentro de
 *    `NumAtCard`. Por eso `message_log.correlation_id` es `text` y no `uuid`.
 */
/**
 * UUID RFC 4122, versiones 1 a 8 (incluye v7, que es el que usamos por ser
 * ordenable por tiempo). Se valida con regex propia y no con `z.string().uuid()`
 * porque la de Zod 3 solo acepta versiones 1 a 5 y rechazaria nuestros v7.
 * El UUID nulo (todo ceros) queda fuera a proposito: un correlation id vacio es
 * un bug, no un valor.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/** UUID generico (mismo criterio que el correlation id): ids internos, eventos. */
export const uuidSchema = z
    .string()
    .trim()
    .regex(UUID_PATTERN, 'Se esperaba un UUID (RFC 4122, version 1 a 8).');
export const correlationIdSchema = z
    .string()
    .trim()
    .regex(UUID_PATTERN, 'El correlation id debe ser un UUID (RFC 4122, version 1 a 8).');
/**
 * Referencia de correlacion tal como la transporta un sistema externo.
 * Texto corto y no vacio: numero de documento, folio, id de pedido del canal.
 */
export const externalCorrelationRefSchema = z
    .string()
    .trim()
    .min(1, 'La referencia de correlacion no puede venir vacia.')
    .max(128, 'La referencia de correlacion no puede superar 128 caracteres.');
/**
 * Correlation id tal como se guarda en el message log: acepta el UUID canonico
 * y tambien la referencia externa, porque el log tiene que poder trazar un
 * mensaje de Altanet cuyo unico hilo es `"30045"`.
 */
export const messageCorrelationIdSchema = externalCorrelationRefSchema;
/** `true` si el valor es un UUID valido para usarse como correlation id. */
export function isCorrelationId(value) {
    return typeof value === 'string' && UUID_PATTERN.test(value.trim());
}
/**
 * Genera un correlation id nuevo.
 *
 * Se apoya en `crypto.randomUUID()` del runtime (Node 18+ y todo navegador
 * moderno) para no arrastrar dependencias en un paquete que DecoraPOS compila.
 * Si el runtime no lo expone, falla ruidosamente: generar un id debil seria
 * peor que no generarlo.
 */
export function newCorrelationId() {
    const runtimeCrypto = globalThis.crypto;
    if (!runtimeCrypto || typeof runtimeCrypto.randomUUID !== 'function') {
        throw new Error('Este runtime no expone crypto.randomUUID(): genera el correlation id con una libreria de UUID.');
    }
    return runtimeCrypto.randomUUID();
}
/**
 * Clave de idempotencia canonica de la plataforma.
 *
 * Convencion: `<sistema>:<entidad>:<id externo>` (p. ej.
 * `decorapos:order:POS-2026-000123`). Todo receptor deduplica por esta clave y
 * la tabla `message_log` tiene UNIQUE (system, idempotency_key), asi que dos
 * emisores no pueden pisarse aunque usen el mismo id.
 */
export function buildIdempotencyKey(system, entity, ...parts) {
    const segments = [system, entity, ...parts]
        .map((part) => String(part).trim())
        .filter((part) => part !== '');
    if (segments.length < 3) {
        throw new Error('La clave de idempotencia necesita sistema, entidad y al menos un identificador no vacio.');
    }
    return segments.join(':');
}
/** Clave de idempotencia: texto corto y no vacio. */
export const idempotencyKeySchema = z
    .string()
    .trim()
    .min(1, 'La clave de idempotencia no puede venir vacia.')
    .max(255, 'La clave de idempotencia no puede superar 255 caracteres.');
//# sourceMappingURL=correlation.js.map